import React, { Fragment, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';
import { FormattedMessageWrappedInSpan } from '../components/misc';

import DvdLabelDialog from '../components/content/dialogs/DvdLabelDialog';
import MobileContainer from '../components/misc/MobileContainer';
import StickyContainer from '../components/misc/StickyContainer';
import {
  CartAdditionalProductDvd,
  CartOverviewItem,
  CartTotal,
} from '../components/content/cart-items';
import { CartQuickCheckout } from '../components/content/cart-quick-checkout';
import { PrimaryButton } from '../components/misc/Buttons';

import { useSimpleForm, useLoadingReducer, useNotificationDialogContext } from '../hooks';
import { getPhotoDvd } from '../utils/common';
import { trackEvent } from '../utils/ga-tracking';

import {
  AddFotoCdToCartMutation,
  ClearCartMutation,
  DeleteLineItemFromCartMutation,
} from '../mutations';

import {
  FOTO_FLAT,
  SINGLE_PHOTO,
  PHOTO_DVD,
  PHOTO_BOOK,
  PHOTO_DVD_EXPLANATION_MSG,
  PHOTO_BOOK_SHIPPING_CHARGES_MSG,
} from '../utils/variables';

function CartPage(props) {
  const { cart } = props.viewer;

  const [loadingItems, dispatch, isObjectLoading] = useLoadingReducer();
  const [showDvdLabelDialog, setShowDvdLabelDialog] = useState(false);
  const [dvdFields, handleDvdFieldsChange] = useSimpleForm([
    { name: 'lineOne' },
    { name: 'lineTwo', initialValue: cart.lineItems[0].product.event.name },
    { name: 'lineThree' },
    { name: 'lineFour' },
  ]);

  const notificationContext = useNotificationDialogContext();

  const additionalDvd = useMemo(() => {
    // Check if the dvd is an additional product
    return getPhotoDvd(cart.additionalProducts);
  }, [cart.additionalProducts]);

  function addFotoCdToCart(productId, dvdText) {
    dispatch({ type: 'add', payload: productId });

    AddFotoCdToCartMutation.commit(
      props.relay.environment,
      {
        productId,
        textLine1: dvdText.lineOne,
        textLine2: dvdText.lineTwo,
        textLine3: dvdText.lineThree,
        textLine4: dvdText.lineFour,
      },
      (error, cart) => {
        dispatch({ type: 'remove', payload: productId });
      }
    );
  }

  function removeItemFromCart(lineItemId) {
    dispatch({ type: 'add', payload: lineItemId });

    const { cart } = props.viewer;

    // If the last item remaining in the cart should be removed, call the 'clearCart'
    // callback immediately. Will NOT be triggered if multiple items are deleted from the
    // cart at the same time.
    if (cart.lineItems.length === 1) {
      clearCart();
    } else {
      DeleteLineItemFromCartMutation.commit(
        props.relay.environment,
        lineItemId,
        (error, updatedCart) => {
          if (updatedCart) {
            // A DVD can not be bought, if no other products (Foto-Flat, Single Photo)
            // are in the updatedCart. This means that if the only product in the updatedCart is a DVD,
            // the updatedCart should be cleared.
            if (updatedCart.lineItems.length === 1) {
              if (updatedCart.lineItems[0].product.type === PHOTO_DVD) {
                clearCart();
              }
            }

            if (updatedCart.lineItems.length === 0) {
              // This case occurs when multiple items are deleted from the cart at the
              // SAME TIME and the first 'if' case was not triggered.
              clearCart();
            }
          } else {
            // This case occurs if the backend could not found the cart
            clearCart();
          }
          dispatch({ type: 'remove', payload: lineItemId });
        }
      );
    }
  }

  function clearCart() {
    ClearCartMutation.commit(props.relay.environment, props.viewer.cart.id);
  }

  function handleHelpClick(productType) {
    switch (productType) {
      case PHOTO_DVD:
        notificationContext.onOpenNotificationDialog(PHOTO_DVD_EXPLANATION_MSG);
    }
  }

  function handleSubtitleHelpClick(productType) {
    switch (productType) {
      case PHOTO_BOOK:
        notificationContext.onOpenNotificationDialog(PHOTO_BOOK_SHIPPING_CHARGES_MSG);
    }
  }

  function goToCheckout() {
    trackEvent('Checkout', 'Start checkout', 'Proceed to checkout from cart overview');
    props.history.push('/checkout');
  }

  return (
    <Fragment>
      <MobileContainer>
        <StickyContainer>
          <CartQuickCheckout cart={cart} onCheckoutClick={goToCheckout} />
        </StickyContainer>
      </MobileContainer>
      <div className="container-960">
        <div className="cart-page-content">
          <div className="row">
            <div className="col-xs-16 col-sm-14 col-sm-offset-1">
              <h1 className="cart-page-header">
                <FormattedMessageWrappedInSpan
                  id="cartPage.overviewTitle"
                  defaultMessage="Your cart"
                />
              </h1>
              <div className="cart-page-items-list">
                {cart.lineItems.map(li => {
                  return (
                    <CartOverviewItem
                      key={li.id}
                      isDownloadOnly={
                        (li.product.type === FOTO_FLAT ||
                          li.product.type === SINGLE_PHOTO) &&
                        additionalDvd !== undefined
                      }
                      isLoading={isObjectLoading(li.id)}
                      lineItem={li}
                      onHelpClick={() => handleHelpClick(li.product.type)}
                      onDeleteClick={() => removeItemFromCart(li.id)}
                      onSubtitleHelpClick={() => handleSubtitleHelpClick(li.product.type)}
                    />
                  );
                })}
              </div>
              <CartTotal totalPrice={cart.totalPrice} />
            </div>
          </div>
          <div className="row">
            <div className="col-xs-16 col-sm-6 col-sm-offset-1 cart-coupon-code-description">
              <FormattedMessageWrappedInSpan id="cartCoupon.description" defaultMessage="If you have a coupon, you can apply it on checkout" />
            </div>
            <div className="col-xs-16 col-sm-6 col-sm-offset-2">
              <PrimaryButton
                disabled={loadingItems.length !== 0}
                hasFullWidth={true}
                leftLabel={
                  <FormattedMessageWrappedInSpan
                    id="cartPage.placeOrderButton"
                    defaultMessage="Proceed to checkout"
                  />
                }
                onClick={goToCheckout}
              />
            </div>
          </div>
        </div>
        {additionalDvd && (
          <Fragment>
            <div className="row">
              <div className="col-xs-16 col-sm-14 col-sm-offset-1">
                <div className="cart-page-content section-separator">
                  <h1 className="cart-page-header">
                    <FormattedMessageWrappedInSpan
                      id="cartPage.addiTionalProductsTitle"
                      defaultMessage="Additional Products"
                    />
                  </h1>
                  <div className="cart-page-items-list">
                    <CartAdditionalProductDvd
                      additionalProduct={additionalDvd}
                      isLoading={isObjectLoading(additionalDvd.id)}
                      onAddToCartClick={() => setShowDvdLabelDialog(true)} // Before adding the DVD to the cart, the user can customize it
                      onHelpClick={() => handleHelpClick(additionalDvd.type)}
                    />
                  </div>
                </div>
              </div>
            </div>
            <DvdLabelDialog
              open={showDvdLabelDialog}
              dvdText={dvdFields}
              onTextChange={handleDvdFieldsChange}
              onRequestClose={() => setShowDvdLabelDialog(false)}
              onAcceptClick={() => {
                setShowDvdLabelDialog(false);
                addFotoCdToCart(additionalDvd.id, dvdFields);
              }}
            />
          </Fragment>
        )}
      </div>
    </Fragment>
  );
}

CartPage.propTypes = {
  // react-router props
  history: PropTypes.object,

  // relay props
  relay: PropTypes.object,
  viewer: PropTypes.object,
};

export default createFragmentContainer(CartPage, {
  viewer: graphql`
    fragment CartPage_viewer on Viewer {
      cart {
        additionalProducts {
          id
          type
          ...CartAdditionalProductDvd_additionalProduct
        }
        id
        lineItems {
          id
          product {
            event {
              name
            }
            type
          }
          ...CartOverviewItem_lineItem
        }
        totalPrice

        ...CartQuickCheckout_cart
      }
    }
  `,
});
